import { BillingCenterSelect } from '@/components/BillingCenterSelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAddTravelEsimToCheckout } from '@/hooks/useCatalogActions';
import { useDefaultSite } from '@/hooks/useDefaultSite';
import { useLine } from '@/hooks/useLines';
import { useBeneficiaryTravelEsims } from '@/hooks/useTravelEsim';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { formatPrice } from '@/lib/format';
import {
  formatLineHolder,
  formatTravelEsimData,
  formatTravelEsimDuration,
  formatTravelEsimPricePerDay,
} from '@/lib/travel-esim';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import type { MobileLine, TravelEsimQuote } from '@octave/contracts';
import { Check, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  DuplicateAlert,
  LineSearch,
} from './TravelEsimBeneficiaryFields';
import { TravelEsimTargetNotice } from './TravelEsimTargetNotice';

const BeneficiaryFormSchema = z
  .object({
    kind: z.enum(['LINE', 'EXTERNAL']),
    lineId: z.string().optional(),
    name: z.string().optional(),
    email: z.string().trim().email('Adresse e-mail invalide'),
    siteId: z.string().min(1, 'Choisissez un centre de facturation'),
  })
  .superRefine((form, ctx) => {
    if (form.kind === 'LINE' && !form.lineId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lineId'],
        message: 'Choisissez une ligne de la flotte',
      });
    }
    if (form.kind === 'EXTERNAL' && !form.name?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['name'],
        message: 'Indiquez le nom du bénéficiaire',
      });
    }
  });

type BeneficiaryForm = z.infer<typeof BeneficiaryFormSchema>;

interface TravelEsimOrderDialogProps {
  quote: TravelEsimQuote;
  /** Line to pre-attach (deep link from the line detail). */
  initialLineId?: string;
  onClose: () => void;
}

/**
 * Order modal of the international eSIM, in three states: attach to a fleet
 * line (search, e-mail pre-filled from the holder but editable), attach to
 * an outsider (name + e-mail, non-blocking alert when the e-mail already
 * belongs to the fleet), then the "added to cart" confirmation. Changing
 * the mode resets name, line and e-mail (spec rule 8).
 */
export function TravelEsimOrderDialog({
  quote,
  initialLineId,
  onClose,
}: TravelEsimOrderDialogProps) {
  const { data: organization } = useCurrentOrganization();
  const { currentSite } = useDefaultSite();
  const addToCheckout = useAddTravelEsimToCheckout();
  const [selectedLine, setSelectedLine] = useState<MobileLine | null>(null);
  const [added, setAdded] = useState(false);

  const form = useForm<BeneficiaryForm>({
    resolver: zodResolver(BeneficiaryFormSchema),
    defaultValues: {
      kind: 'LINE',
      lineId: initialLineId ?? '',
      name: '',
      email: '',
      siteId: currentSite?.id ?? '',
    },
  });
  const kind = form.watch('kind');
  const email = form.watch('email');
  const siteId = form.watch('siteId');

  // Does the beneficiary already hold an eSIM? Then the plan is a recharge
  // of it by default; « nouvelle eSIM » stays one click away.
  const [newEsim, setNewEsim] = useState(false);
  const externalEmail =
    kind === 'EXTERNAL' && z.string().email().safeParse(email).success
      ? email.trim()
      : undefined;
  const { data: existingEsims } = useBeneficiaryTravelEsims(organization?.id, {
    lineId: kind === 'LINE' ? selectedLine?.id : undefined,
    email: externalEmail,
  });
  const existingEsim = existingEsims?.[0];
  const recharge = !!existingEsim && !newEsim;
  const beneficiaryKnown = kind === 'LINE' ? !!selectedLine : !!externalEmail;

  // Deep link: the line detail opens the modal on its own line.
  const { data: initialLine } = useLine(
    selectedLine || !initialLineId ? undefined : initialLineId,
  );
  const attachLine = useCallback(
    (line: MobileLine) => {
      setSelectedLine(line);
      form.setValue('lineId', line.id, { shouldValidate: true });
      form.setValue('email', line.identity?.email ?? '', {
        shouldValidate: !!line.identity?.email,
      });
    },
    [form],
  );
  useEffect(() => {
    if (initialLine) attachLine(initialLine);
  }, [initialLine, attachLine]);

  const switchKind = (next: 'LINE' | 'EXTERNAL') => {
    if (next === kind) return;
    setSelectedLine(null);
    setNewEsim(false);
    form.reset({ kind: next, lineId: '', name: '', email: '', siteId });
  };

  const submit = form.handleSubmit(async (values) => {
    if (!organization) return;
    await addToCheckout.mutateAsync({
      organizationId: organization.id,
      country: quote.country,
      siteId: values.siteId,
      departureDate: quote.departureDate,
      returnDate: quote.returnDate,
      beneficiary:
        values.kind === 'LINE'
          ? { kind: 'LINE', lineId: values.lineId!, email: values.email }
          : { kind: 'EXTERNAL', name: values.name!.trim(), email: values.email },
      travelEsimId: recharge ? existingEsim.id : undefined,
      newEsim: existingEsim && newEsim ? true : undefined,
    });
    setAdded(true);
  });

  const beneficiaryLabel =
    kind === 'LINE'
      ? selectedLine
        ? formatLineHolder(selectedLine)
        : ''
      : (form.getValues('name') ?? '');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="!max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-slate-900">
            {formatTravelEsimData(quote.dataLimitGb)} ·{' '}
            {formatTravelEsimDuration(quote.stayDurationDays)}
          </DialogTitle>
          <p className="text-sm text-slate-500">
            {quote.country} · {formatPrice(quote.totalPriceCents)} HT, soit{' '}
            {formatTravelEsimPricePerDay(quote.pricePerDayCents)}
          </p>
        </DialogHeader>

        {added ? (
          <div className="space-y-5">
            <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
                <Check className="h-3.5 w-3.5" />
              </span>
              <div className="text-sm">
                <p className="font-semibold text-emerald-800">
                  {recharge ? 'Recharge ajoutée au panier' : 'eSIM ajoutée au panier'}
                </p>
                <p className="text-emerald-700">
                  {formatTravelEsimData(quote.dataLimitGb)} · {quote.country} ·{' '}
                  {formatTravelEsimDuration(quote.stayDurationDays)}, pour{' '}
                  {beneficiaryLabel} ({email}). Finalisez la commande depuis le
                  panier.
                </p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={onClose}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5"
              >
                Terminer
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-slate-700">
                À qui rattacher cette eSIM ?
              </legend>
              <div className="grid grid-cols-2 gap-3">
                <KindCard
                  active={kind === 'LINE'}
                  title="Un membre de la flotte"
                  subtitle="Titulaire d'un forfait Eiko existant"
                  onClick={() => switchKind('LINE')}
                />
                <KindCard
                  active={kind === 'EXTERNAL'}
                  title="Hors flotte"
                  subtitle="Qui n'a pas de ligne mobile Eiko"
                  onClick={() => switchKind('EXTERNAL')}
                />
              </div>
            </fieldset>

            {kind === 'LINE' ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Ligne de la flotte
                </label>
                {selectedLine ? (
                  <div className="flex items-center justify-between gap-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm">
                    <span className="text-slate-800">
                      {formatLineHolder(selectedLine)} · {selectedLine.number}
                      {selectedLine.phonePlan
                        ? ` · ${selectedLine.phonePlan.name}`
                        : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedLine(null);
                        form.setValue('lineId', '');
                        form.setValue('email', '');
                      }}
                      className="text-xs font-medium text-emerald-700 underline cursor-pointer"
                    >
                      Changer
                    </button>
                  </div>
                ) : (
                  <LineSearch
                    organizationId={organization?.id}
                    onSelect={attachLine}
                  />
                )}
                <FieldError message={form.formState.errors.lineId?.message} />
              </div>
            ) : (
              <div className="space-y-2">
                <label
                  htmlFor="travel-esim-name"
                  className="block text-sm font-medium text-slate-700"
                >
                  Nom du bénéficiaire
                </label>
                <Input
                  id="travel-esim-name"
                  placeholder="Prénom et nom"
                  className="h-11 rounded-xl"
                  {...form.register('name')}
                />
                <FieldError message={form.formState.errors.name?.message} />
              </div>
            )}

            <div className="space-y-2">
              <label
                htmlFor="travel-esim-email"
                className="block text-sm font-medium text-slate-700"
              >
                E-mail de réception du QR code
              </label>
              <Input
                id="travel-esim-email"
                type="email"
                placeholder="prenom.nom@entreprise.fr"
                className="h-11 rounded-xl"
                {...form.register('email')}
              />
              <FieldError message={form.formState.errors.email?.message} />
              <p className="text-xs text-slate-400">
                {kind === 'LINE'
                  ? 'Pré-rempli depuis la fiche du collaborateur, modifiable.'
                  : "Le bénéficiaire reçoit le QR code et la notice d'installation."}
              </p>
              {kind === 'EXTERNAL' && (
                <DuplicateAlert
                  organizationId={organization?.id}
                  email={email}
                  onAttach={(line) => {
                    switchKind('LINE');
                    attachLine(line);
                  }}
                />
              )}
            </div>

            {beneficiaryKnown && (
              <TravelEsimTargetNotice
                existingEsim={existingEsim}
                newEsim={newEsim}
                beneficiaryLabel={beneficiaryLabel}
                email={email}
                onNewEsimChange={setNewEsim}
              />
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">
                Centre de facturation
              </label>
              <BillingCenterSelect
                value={siteId || null}
                onChange={(value) =>
                  form.setValue('siteId', value, { shouldValidate: true })
                }
              />
              <FieldError message={form.formState.errors.siteId?.message} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="rounded-full"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={addToCheckout.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5 gap-2"
              >
                {addToCheckout.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {recharge ? 'Ajouter la recharge au panier' : 'Ajouter au panier'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KindCard({
  active,
  title,
  subtitle,
  onClick,
}: {
  active: boolean;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-xl border px-4 py-3 text-left transition-all cursor-pointer',
        active
          ? 'border-emerald-500 bg-emerald-50'
          : 'border-slate-200 hover:border-emerald-200',
      )}
    >
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="text-xs text-slate-500">{subtitle}</p>
    </button>
  );
}

function FieldError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return <p className="text-xs text-red-600">{message}</p>;
}
